import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClinicDialog from './ClinicDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import AppointmentTypeManager from './AppointmentTypeManager';
import PractitionerPhotoManager from './PractitionerPhotoManager';
import { getClinics, deleteClinic } from '../services/adminService';

const ClinicManager = ({ onClinicSelect }) => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedClinicForAppointments, setSelectedClinicForAppointments] = useState(null);
  const [selectedClinicForPhotos, setSelectedClinicForPhotos] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState(null); // For tracking selected clinic
  
  const fetchClinics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClinics();
      setClinics(data.clinics || []);
    } catch (err) {
      setError(err.message || 'Failed to load clinics');
      console.error('Error loading clinics:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchClinics();
  }, []);
  
  const handleAddClick = () => {
    setSelectedClinic(null);
    setOpenDialog(true);
  };
  
  const handleEditClick = (clinic) => {
    setSelectedClinic(clinic);
    setOpenDialog(true);
  };
  
  const handleDeleteClick = (clinic) => {
    setSelectedClinic(clinic);
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedClinic) return;
    
    try {
      setLoading(true);
      await deleteClinic(selectedClinic.id);
      setOpenDeleteDialog(false);
      await fetchClinics(); // Refresh the list
    } catch (err) {
      setError(err.message || 'Failed to delete clinic');
      console.error('Error deleting clinic:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDialogClose = (refreshNeeded) => {
    setOpenDialog(false);
    if (refreshNeeded) {
      fetchClinics();
    }
  };
  
  const copyBookingLink = (clinic) => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/booking/${clinic.stub}`;
    
    navigator.clipboard.writeText(url)
      .then(() => {
        alert('Booking link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy booking link:', err);
        alert('Failed to copy booking link. Please copy it manually: ' + url);
      });
  };
  
  const handleManageAppointmentTypes = (clinic) => {
    setSelectedClinicForAppointments(clinic);
    setSelectedClinicForPhotos(null);
  };
  
  const handleManagePhotos = (clinic) => {
    // If onClinicSelect prop is available, use it to communicate with parent
    if (onClinicSelect) {
      onClinicSelect(clinic.id);
    }
    
    // For backwards compatibility
    setSelectedClinicForPhotos(clinic);
    setSelectedClinicForAppointments(null);
  };
  
  const handleManageServices = (clinic) => {
    // Set the selected clinic for practitioner services
    setSelectedClinicId(clinic.id);
    if (onClinicSelect) {
      onClinicSelect(clinic.id);
    }
  };
  
  const handleSelectClinic = (clinic) => {
    // Toggle selection
    if (selectedClinicId === clinic.id) {
      setSelectedClinicId(null);
      if (onClinicSelect) onClinicSelect(null);
    } else {
      setSelectedClinicId(clinic.id);
      if (onClinicSelect) onClinicSelect(clinic.id);
    }
  };
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {!selectedClinicForAppointments && !selectedClinicForPhotos ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Clinic Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              Add Clinic
            </Button>
          </Box>
          
          {loading && !clinics.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Business ID</TableCell>
                    <TableCell>Booking URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clinics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No clinics found. Click "Add Clinic" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clinics.map((clinic) => (
                      <TableRow 
                        key={clinic.id}
                        sx={{
                          backgroundColor: selectedClinicId === clinic.id ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                          '&:hover': {
                            backgroundColor: selectedClinicId === clinic.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                        onClick={() => handleSelectClinic(clinic)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {selectedClinicId === clinic.id && (
                              <CheckCircleIcon 
                                color="primary" 
                                fontSize="small" 
                                sx={{ mr: 1 }}
                              />
                            )}
                            {clinic.name}
                          </Box>
                        </TableCell>
                        <TableCell>{clinic.business_id}</TableCell>
                        <TableCell>
                          {`/booking/${clinic.stub}`}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              copyBookingLink(clinic);
                            }}
                            title="Copy booking link"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={clinic.is_active ? 'Active' : 'Inactive'} 
                            color={clinic.is_active ? 'success' : 'default'}
                            size="small"
                            onClick={(e) => e.stopPropagation()} // Prevent row click
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Manage Appointment Types">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleManageAppointmentTypes(clinic);
                              }}
                              color="primary"
                            >
                              <EventNoteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Manage Practitioner Photos">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleManagePhotos(clinic);
                              }}
                              color="primary"
                            >
                              <PhotoLibraryIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Manage Practitioner Services">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleManageServices(clinic);
                              }}
                              color="primary"
                            >
                              <MedicalServicesIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View booking page">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                const baseUrl = window.location.origin;
                                const url = `${baseUrl}/${clinic.stub}`;
                                window.open(url, '_blank');
                              }}
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit clinic">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleEditClick(clinic);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete clinic">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleDeleteClick(clinic);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : selectedClinicForAppointments ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Appointment Types: {selectedClinicForAppointments.name}
            </Typography>
            <Button 
              variant="outlined"
              onClick={() => setSelectedClinicForAppointments(null)}
            >
              Back to Clinics
            </Button>
          </Box>
          <AppointmentTypeManager clinicId={selectedClinicForAppointments.id} />
        </>
      ) : selectedClinicForPhotos && !onClinicSelect ? (
        // Only render this if we're not using the parent component's UI
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Practitioner Photos: {selectedClinicForPhotos.name}
            </Typography>
            <Button 
              variant="outlined"
              onClick={() => setSelectedClinicForPhotos(null)}
            >
              Back to Clinics
            </Button>
          </Box>
          {/* Use the clinic ID instead of the whole clinic object */}
          <PractitionerPhotoManager clinicId={selectedClinicForPhotos.id} />
        </>
      ) : null}
      
      {openDialog && (
        <ClinicDialog
          open={openDialog}
          onClose={handleDialogClose}
          clinic={selectedClinic}
        />
      )}
      
      {openDeleteDialog && selectedClinic && (
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          clinicName={selectedClinic.name}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default ClinicManager;